import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/components/base/NewsCard";
import ShareModal from "@/components/base/ShareModal";
import { getNewsById, getRelatedNews } from "@/lib/catalog";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const item = getNewsById(id);
  const [shareOpen, setShareOpen] = useState(false);

  useSeo({
    title: item ? `${item.title} - 影视资讯 - 姐姐的朋友` : "影视资讯 - 姐姐的朋友",
    description: item ? item.summary : "姐姐的朋友影视资讯频道，提供行业动态、新片报道与主创访谈。",
    keywords: item
      ? `${item.category},${item.title},影视资讯,姐姐的朋友`
      : "影视资讯,行业动态,新片报道,姐姐的朋友",
  });

  if (!item) {
    return (
      <div className="flex min-h-screen flex-col bg-background-100">
        <SiteHeader />
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-16 md:px-6">
          <EmptyState
            title="资讯内容不存在"
            description="这篇文章可能已被删除，或链接地址有误。你可以回到资讯列表继续浏览。"
            actionLabel="返回影视资讯"
            onAction={() => navigate("/news")}
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedNews(item, 3);

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-9">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "影视资讯", to: "/news" }, { label: item.title }]}
        />

        <article className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <header>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-accent-100 px-2.5 py-1 text-accent-900">{item.category}</span>
                <span className="text-foreground-500">{item.date}</span>
                <span className="text-foreground-400">{item.author}</span>
              </div>
              <h1 className="mt-3 text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">{item.title}</h1>
              <p className="mt-3 text-sm leading-7 text-foreground-600">{item.summary}</p>
            </header>

            <div className="mt-5 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={item.image}
                alt={`${item.title} 关键配图`}
                title={`${item.title} - 影视资讯 - 姐姐的朋友`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <div className="space-y-5">
                {item.content.map((paragraph, index) => (
                  <p key={index} className="text-sm leading-8 text-foreground-700 md:text-[15px] md:leading-8">
                    {paragraph}
                  </p>
                ))}
              </div>

              <blockquote className="mt-6 border-l-2 border-accent-500 bg-accent-50 px-4 py-3 text-sm leading-7 text-foreground-800">
                「姐姐的朋友」编辑部：本文为影视内容介绍与行业观察，不涉及任何在线播放或下载资源。
              </blockquote>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-5">
                <div className="flex flex-wrap gap-2">
                  {[item.category, "影视观察", "内容报道"].map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-[11px] text-secondary-900">
                      #{tag}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line text-sm" />
                  分享这篇文章
                </button>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-background-50 px-5 py-4">
              <Link
                to="/news"
                className="flex cursor-pointer items-center gap-1 text-xs text-foreground-600 transition-colors hover:text-primary-600"
              >
                <i className="ri-arrow-left-line text-sm" />
                返回资讯列表
              </Link>
              <Link
                to="/category"
                className="flex cursor-pointer items-center gap-1 text-xs text-primary-600 transition-colors hover:text-primary-700"
              >
                去影视分类看看
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="flex flex-col gap-6 lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  相关文章
                </h2>
                <div className="mt-2">
                  {related.map((news) => (
                    <Link
                      key={news.id}
                      to={`/news/${news.id}`}
                      className="group block cursor-pointer border-b border-background-200 py-3 last:border-b-0"
                    >
                      <p className="line-clamp-2 text-sm leading-6 text-foreground-800 transition-colors group-hover:text-primary-600">
                        {news.title}
                      </p>
                      <p className="mt-1 font-label text-[11px] text-foreground-400">
                        {news.date} · {news.category}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-primary-950 p-5 text-background-50">
                <p className="font-label text-[10px] tracking-[0.24em] text-primary-400">EXPLORE</p>
                <h2 className="mt-2 text-sm font-bold">看完资讯，去找片子</h2>
                <p className="mt-2 text-xs leading-6 text-primary-200">
                  分类页支持按类型、地区、年份筛选，并可按评分与热度排序。
                </p>
                <Link
                  to="/category"
                  className="mt-4 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  浏览影视分类
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>
            </div>
          </aside>
        </article>

        <section className="mt-10 md:mt-14">
          <div className="flex items-end justify-between gap-3">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-primary-500" />
              更多影视资讯
            </h2>
            <Link
              to="/news"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
            >
              全部资讯
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            {related.map((news) => (
              <NewsCard key={`related-${news.id}`} item={news} variant="feature" />
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={`${item.title} - 姐姐的朋友`} />
    </div>
  );
}